import { useState } from 'react';
import { useAIConsole } from '../../contexts/AIConsoleContext';
import { useAgentConversation } from '../../hooks/useAgentConversation';
import { NovelChapterSelector } from '../../components/llm';
import { ConversationTree } from '../../components/agent';

const QUICK_TEMPLATES = [
  { label: '摘要分析', message: '請對選定章節使用 extract_summary 工具進行摘要分析' },
  { label: '角色識別', message: '請對選定章節使用 extract_characters 工具識別角色' },
] as const;

export function AgenticTestPanel() {
  const {
    selectedProviderId,
    selectedPresetId,
    maxTokens,
    temperature,
    maxIterations,
  } = useAIConsole();

  // Novel/Chapter state
  const [selectedNovelId, setSelectedNovelId] = useState('');
  const [selectedChapterIds, setSelectedChapterIds] = useState<string[]>([]);

  // User input
  const [userMessage, setUserMessage] = useState('請分析這個章節的內容。');
  const [followUpMessage, setFollowUpMessage] = useState('');

  const {
    conversationRounds,
    currentRoundEvents,
    isExecuting,
    error,
    hasConversation,
    totalTokenUsage,
    handleExecute,
    handleStop,
    handleClear,
  } = useAgentConversation({
    selectedProviderId,
    selectedPresetId,
    selectedNovelId,
    selectedChapterIds,
    maxIterations,
    temperature,
    maxTokens,
  });

  const isFirstRound = !hasConversation;
  const currentInput = isFirstRound ? userMessage : followUpMessage;

  const onExecute = () => {
    handleExecute(currentInput);
    if (!isFirstRound) setFollowUpMessage('');
  };

  const applyTemplate = (message: string) => {
    if (isFirstRound) {
      setUserMessage(message);
    } else {
      setFollowUpMessage(message);
    }
  };

  const handleCharacterAnalysis = () => {
    const name = prompt('請輸入角色名稱：');
    if (name) {
      const msg = `請對選定章節使用 analyze_character 工具分析角色 ${name}`;
      applyTemplate(msg);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info */}
      <div className="p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/50 rounded-card">
        <p className="text-sm text-primary-700 dark:text-primary-300">
          Agent 整合測試：選擇小說章節進行分析，支援多輪對話。驗證 Tools、Streaming、Prompt Caching、Jailbreak 功能。
        </p>
      </div>

      {/* Novel/Chapter Selection - Only show in first round */}
      {isFirstRound && (
        <NovelChapterSelector
          selectedNovelId={selectedNovelId}
          selectedChapterIds={selectedChapterIds}
          onNovelChange={setSelectedNovelId}
          onChapterIdsChange={setSelectedChapterIds}
        />
      )}

      {/* SubAgent Quick Templates */}
      <div className="card p-4">
        <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-2">
          SubAgent 快捷
        </label>
        <div className="flex flex-wrap gap-2">
          {QUICK_TEMPLATES.map((t) => (
            <button
              key={t.label}
              onClick={() => applyTemplate(t.message)}
              className="btn btn-ghost btn-sm text-xs"
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={handleCharacterAnalysis}
            className="btn btn-ghost btn-sm text-xs"
          >
            角色深度分析
          </button>
        </div>
      </div>

      {/* User Message Input */}
      {isFirstRound ? (
        <div className="card p-4">
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            用戶訊息
          </label>
          <textarea
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            rows={4}
            className="textarea w-full"
            placeholder="輸入要對 Agent 說的話..."
          />
        </div>
      ) : (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              繼續對話
            </label>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              第 {conversationRounds.length + 1} 輪
            </span>
          </div>
          <textarea
            value={followUpMessage}
            onChange={(e) => setFollowUpMessage(e.target.value)}
            rows={3}
            className="textarea w-full"
            placeholder="輸入追問或繼續對話的內容..."
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 rounded-button text-sm">
          {error}
        </div>
      )}

      {/* Execute/Stop/Clear Buttons */}
      <div className="flex gap-2">
        <button
          onClick={onExecute}
          disabled={isExecuting || !selectedProviderId || !currentInput.trim()}
          className="btn btn-primary btn-md flex-1"
        >
          {isExecuting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              執行中...
            </span>
          ) : isFirstRound ? (
            '開始對話'
          ) : (
            '繼續對話'
          )}
        </button>
        {isExecuting && (
          <button onClick={handleStop} className="btn btn-destructive btn-md">
            停止
          </button>
        )}
        {hasConversation && !isExecuting && (
          <button onClick={handleClear} className="btn btn-secondary btn-md">
            清除對話
          </button>
        )}
      </div>

      {/* Token Usage Summary */}
      {hasConversation && (
        <div className="p-3 bg-neutral-100 dark:bg-neutral-700 rounded-button text-sm">
          <div className="flex flex-wrap gap-4">
            <span className="font-medium">累計 Token:</span>
            <span>輸入: {totalTokenUsage.inputTokens.toLocaleString()}</span>
            <span>輸出: {totalTokenUsage.outputTokens.toLocaleString()}</span>
            <span className="text-primary-600 dark:text-primary-400">
              總計: {totalTokenUsage.totalTokens.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Conversation Display */}
      {(conversationRounds.length > 0 || currentRoundEvents.length > 0) && (
        <div className="card p-4">
          <h3 className="heading-card text-neutral-900 dark:text-neutral-50 mb-4">
            對話過程
          </h3>
          <ConversationTree
            events={[
              ...conversationRounds.flatMap((r) => r.agentEvents),
              ...currentRoundEvents,
            ]}
            isStreaming={isExecuting}
            rounds={conversationRounds}
            currentRoundEvents={currentRoundEvents}
          />
        </div>
      )}
    </div>
  );
}
